import React from 'react';
import {
  displayRating,
  formatAvailability,
  formatSalary,
  getAvatarGradient,
  getInitials,
  isSkillHighlighted,
} from '../utils/profileDisplay';
import styles from './CandidateCard.module.css';

export const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
};

/**
 * @param {{ profile: object, highlightSkills?: string[], onSelect?: (profile) => void }} props
 */
export default function CandidateCard({ profile, highlightSkills = [], onSelect }) {
  const {
    candidateName,
    skills = [],
    yearsExperience,
    currentPosition,
    headline,
    location,
    samastixRating,
    interviewCount = 0,
    availableFrom,
    expectedSalary,
  } = profile;

  const roleLabel = currentPosition || headline || 'Candidate';
  const visibleSkills = skills.slice(0, 5);
  const extraCount = skills.length - visibleSkills.length;
  const availability = formatAvailability(availableFrom);
  const interviewLabel = `${interviewCount} interview${interviewCount === 1 ? '' : 's'}`;
  const ratingLabel = displayRating(samastixRating);

  const handleOpen = () => {
    onSelect?.(profile);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  return (
    <article
      className={styles.card}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`View profile for ${candidateName}`}
    >
      <div className={styles.ratingBadge}>
        <span className={styles.ratingValue}>
          ★ {ratingLabel != null ? ratingLabel.toFixed(1) : '—'}
        </span>
        <span className={styles.ratingMeta}>{interviewLabel}</span>
      </div>

      <div className={styles.headerRow}>
        <div
          className={styles.avatar}
          style={{ background: getAvatarGradient(candidateName) }}
          aria-hidden="true"
        >
          {getInitials(candidateName)}
        </div>
        <div className={styles.identity}>
          <h3 className={styles.name}>{candidateName}</h3>
          <p className={styles.role}>{roleLabel}</p>
        </div>
      </div>

      <div className={styles.statsRow}>
        <div className={styles.stat}>
          <span className={styles.statIcon} aria-hidden="true">
            ⏱
          </span>
          <span className={styles.statText}>
            {yearsExperience != null ? `${yearsExperience} yrs exp` : '— yrs exp'}
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statIcon} aria-hidden="true">
            📍
          </span>
          <span className={styles.statText}>{location?.trim() || 'Remote'}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statIcon} aria-hidden="true">
            💰
          </span>
          <span className={styles.statText}>{formatSalary(expectedSalary)}</span>
        </div>
      </div>

      {skills.length > 0 && (
        <div className={styles.skills}>
          {visibleSkills.map((skill) => (
            <span
              key={skill}
              className={`${styles.chip} ${
                isSkillHighlighted(skill, highlightSkills) ? styles.chipHighlight : ''
              }`}
            >
              {skill}
            </span>
          ))}
          {extraCount > 0 && (
            <span className={styles.chipMore}>+{extraCount} more</span>
          )}
        </div>
      )}

      <div className={styles.footer}>
        <span
          className={`${styles.availability} ${
            availability.soon ? styles.availabilitySoon : styles.availabilityMuted
          }`}
        >
          {availability.label}
        </span>
        <span className={styles.viewLink}>View Profile →</span>
      </div>
    </article>
  );
}
